import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useMobileUploadQueue, type MobileUploadItem } from '../hooks/useMobileUploadQueue'
import {
  ensureMobileAccessToken,
  isVideoCaptureFlow,
  MOBILE_CAPTURE_VIDEO_ACCEPT,
  refreshMobileAuth,
  type MobileCaptureTargetFlow,
} from '../mobileCaptureApi'
import './MobileCapturePage.css'

type Props = {
  workspaceName: string
  /** Which desktop entry point this capture session belongs to — only
   * changes copy/labels here, never routing logic (that's decided
   * server-side by the auth session's own `target_flow`, see
   * `saas_mobile_capture.py::mobile_capture_upload`). */
  targetFlow?: MobileCaptureTargetFlow
  getAccessToken: () => string | null
  onAccessToken: (token: string) => void
  onAuthExpired: () => void
  onLogout: () => void
}

function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true
  const lower = file.name.toLowerCase()
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm')
}

function uploadingStatusLabel(item: MobileUploadItem): string {
  switch (item.status) {
    case 'queued':
      return item.error || '等待上传'
    case 'uploading':
      return item.progress > 0 ? `上传中 ${item.progress}%` : '上传中'
    case 'failed':
      return item.error || '上传失败'
    case 'auth_required':
      return item.error || '连接已失效'
    default:
      return ''
  }
}

/**
 * Three-stage Mobile Capture UI:
 * Pending → Uploading → Uploaded History (session-local only).
 */
export default function MobileCapturePage({
  workspaceName,
  targetFlow = 'item_image_processing',
  getAccessToken,
  onAccessToken,
  onAuthExpired,
  onLogout,
}: Props) {
  const isVideoFlow = isVideoCaptureFlow(targetFlow)
  const flowLabel = isVideoFlow
    ? '上传视频资产'
    : targetFlow === 'flat_image_processing'
      ? '拍摄平面图像'
      : targetFlow === 'workspace_asset_library'
        ? '拍摄图片资产'
        : '拍摄物品图片'
  const mediaAccept = isVideoFlow ? MOBILE_CAPTURE_VIDEO_ACCEPT : 'image/*'
  const [toast, setToast] = useState<string | null>(null)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const cameraRef = useRef<HTMLInputElement | null>(null)
  const galleryRef = useRef<HTMLInputElement | null>(null)
  const toastTimerRef = useRef<number | undefined>(undefined)
  const onAuthExpiredRef = useRef(onAuthExpired)
  onAuthExpiredRef.current = onAuthExpired

  const resolveToken = useCallback(async () => {
    const current = getAccessToken()
    const tok = await ensureMobileAccessToken(current, onAccessToken)
    if (!tok) {
      onAuthExpiredRef.current()
      return null
    }
    if (tok !== current) onAccessToken(tok)
    return tok
  }, [getAccessToken, onAccessToken])

  const {
    pendingItems,
    uploadingItems,
    uploadedHistory,
    counts,
    hasActiveUploads,
    authExpired,
    enqueueFiles,
    startUpload,
    retryItem,
    removePendingItem,
    clearPending,
  } = useMobileUploadQueue({
    getAccessToken,
    acceptVideos: isVideoFlow,
  })

  const renderThumb = (url: string, isVideo: boolean) =>
    isVideo ? (
      <video src={url} muted playsInline preload="metadata" />
    ) : (
      <img src={url} alt="" />
    )

  useEffect(() => {
    if (authExpired) onAuthExpiredRef.current()
  }, [authExpired])

  useEffect(() => {
    void resolveToken()
  }, [resolveToken])

  const showToast = (msg: string) => {
    setToast(msg)
    if (toastTimerRef.current !== undefined) window.clearTimeout(toastTimerRef.current)
    toastTimerRef.current = window.setTimeout(() => setToast(null), 2200)
  }

  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== undefined) window.clearTimeout(toastTimerRef.current)
    }
  }, [])

  const onCameraChange = (e: ChangeEvent<HTMLInputElement>) => {
    const n = enqueueFiles(e.target.files, 'camera')
    e.target.value = ''
    if (n > 0) {
      showToast(
        n === 1
          ? '已加入待上传'
          : isVideoFlow
            ? `已加入 ${n} 个待上传视频`
            : `已加入 ${n} 张待上传`,
      )
    }
  }

  const onGalleryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const n = enqueueFiles(e.target.files, 'gallery')
    e.target.value = ''
    if (n > 0) {
      showToast(
        n === 1
          ? '已加入待上传'
          : isVideoFlow
            ? `已加入 ${n} 个待上传视频`
            : `已加入 ${n} 张待上传`,
      )
    }
  }

  const onUploadAll = async () => {
    try {
      const refreshed = await refreshMobileAuth()
      onAccessToken(refreshed.access_token)
    } catch {
      /* worker will surface auth_required */
    }
    const n = startUpload()
    if (n > 0) {
      showToast(
        n === 1
          ? isVideoFlow
            ? '开始上传 1 个视频…'
            : '开始上传 1 张…'
          : isVideoFlow
            ? `开始上传 ${n} 个视频…`
            : `开始上传 ${n} 张…`,
      )
    }
  }

  const captureDisabled = authExpired
  const pendingCount = counts.pending
  const previewItem = previewId ? pendingItems.find((it) => it.id === previewId) : null

  return (
    <div className="mc-page">
      <header className="mc-header">
        <div className="mc-brand">VISTOERA</div>
        <h1>{flowLabel}</h1>
        <p className="mc-subtitle">
          {workspaceName || '当前工作区'}
          <br />
          先加入待上传 → 确认后后台上传 → 下方保留本次已上传记录
        </p>
        <p className="mc-subtitle mc-format-hint" data-testid="mc-format-hint">
          {isVideoFlow ? (
            <>支持 MP4 / MOV / WEBM 视频文件</>
          ) : (
            <>
              支持 JPEG / PNG / WebP，暂不支持 HEIC/HEIF
              <br />
              iPhone 用户请在「设置 → 相机 → 格式」选择「兼容性最佳」后拍摄
            </>
          )}
        </p>
        <button type="button" className="mc-logout-link" onClick={onLogout}>
          退出登录
        </button>
      </header>

      <div className="mc-capture-buttons">
        <button
          type="button"
          className="mc-big-button mc-primary"
          disabled={captureDisabled}
          data-testid="mc-camera-btn"
          onClick={() => cameraRef.current?.click()}
        >
          {isVideoFlow ? '拍摄视频' : '拍照'}
        </button>
        <input
          ref={cameraRef}
          type="file"
          accept={mediaAccept}
          capture={isVideoFlow ? undefined : 'environment'}
          className="mc-hidden-file"
          disabled={captureDisabled}
          data-testid="mc-camera-input"
          onChange={onCameraChange}
        />

        <button
          type="button"
          className="mc-big-button mc-secondary"
          disabled={captureDisabled}
          data-testid="mc-gallery-btn"
          onClick={() => galleryRef.current?.click()}
        >
          {isVideoFlow ? '选择手机视频' : '从相册选择'}
        </button>
        <input
          ref={galleryRef}
          type="file"
          accept={mediaAccept}
          multiple={!isVideoFlow}
          className="mc-hidden-file"
          disabled={captureDisabled}
          data-testid="mc-gallery-input"
          onChange={onGalleryChange}
        />
      </div>

      {toast && <div className="mc-banner mc-banner-info">{toast}</div>}

      {authExpired && (
        <div className="mc-banner mc-banner-error" data-testid="mc-auth-expired">
          连接已失效，请重新登录。未上传的照片仍保留在下方列表中。
        </div>
      )}

      {/* ① Pending */}
      <section className="mc-section" data-testid="mc-pending-section">
        <div className="mc-section-head">
          <h2 className="mc-section-title">
            待上传 <span className="mc-section-count">{pendingCount}</span>
          </h2>
          {pendingCount > 1 && (
            <button type="button" className="mc-section-link" onClick={() => clearPending()}>
              清空
            </button>
          )}
        </div>
        {pendingCount === 0 ? (
          <p className="mc-section-empty">{isVideoFlow ? '暂无待上传视频' : '暂无待上传图片'}</p>
        ) : (
          <>
            <div className="mc-thumb-grid mc-pending-grid" data-testid="mc-pending-grid">
              {pendingItems.map((item) => (
                <div className="mc-thumb-cell" key={item.id}>
                  <button
                    type="button"
                    className="mc-thumb-btn"
                    onClick={() => setPreviewId(item.id)}
                    aria-label="预览"
                  >
                    {renderThumb(item.previewUrl, isVideoFile(item.file))}
                  </button>
                  <button
                    type="button"
                    className="mc-thumb-remove"
                    aria-label="删除"
                    data-testid={`mc-pending-remove-${item.id}`}
                    onClick={() => {
                      if (previewId === item.id) setPreviewId(null)
                      removePendingItem(item.id)
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mc-big-button mc-upload"
              disabled={captureDisabled || pendingCount === 0}
              data-testid="mc-upload-btn"
              onClick={() => void onUploadAll()}
            >
              上传全部（{pendingCount}）
            </button>
          </>
        )}
      </section>

      {/* ② Uploading */}
      {counts.inFlight > 0 && (
        <section className="mc-section" data-testid="mc-uploading-section">
          <div className="mc-section-head">
            <h2 className="mc-section-title">
              上传中 <span className="mc-section-count">{counts.inFlight}</span>
            </h2>
            {hasActiveUploads && <span className="mc-active-hint">可继续拍照</span>}
          </div>
          <div className="mc-queue" data-testid="mc-uploading-queue">
            {uploadingItems.map((item) => (
              <div className={`mc-queue-row mc-status-${item.status}`} key={item.id}>
                <div className="mc-queue-thumb">
                  {renderThumb(item.previewUrl, isVideoFile(item.file))}
                </div>
                <div className="mc-queue-meta">
                  <div className="mc-queue-name">
                    {item.source === 'camera' ? '拍照' : '相册'}
                  </div>
                  <div className="mc-queue-status">{uploadingStatusLabel(item)}</div>
                  {item.status === 'uploading' && item.progress > 0 && (
                    <div className="mc-progress">
                      <div className="mc-progress-bar" style={{ width: `${item.progress}%` }} />
                    </div>
                  )}
                </div>
                {(item.status === 'failed' || item.status === 'auth_required') && !authExpired && (
                  <button
                    type="button"
                    className="mc-retry-btn"
                    data-testid={`mc-retry-${item.id}`}
                    onClick={() => retryItem(item.id)}
                  >
                    重试
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ③ Uploaded History — session only, never from asset library */}
      {uploadedHistory.length > 0 && (
        <section className="mc-section" data-testid="mc-uploaded-section">
          <div className="mc-section-head">
            <h2 className="mc-section-title">
              已上传 <span className="mc-section-count">{uploadedHistory.length}</span>
            </h2>
            <span className="mc-section-note">本次会话</span>
          </div>
          <div className="mc-thumb-grid mc-history-grid" data-testid="mc-uploaded-grid">
            {uploadedHistory.map((item) => (
              <div className="mc-history-cell" key={item.id} title={item.assetId}>
                {item.serverThumbnailUrl || !isVideoFlow
                  ? <img src={item.thumbnailUrl} alt="" loading="lazy" />
                  : <video src={item.thumbnailUrl} muted playsInline preload="metadata" />}
              </div>
            ))}
          </div>
        </section>
      )}

      {pendingCount === 0 && counts.inFlight === 0 && uploadedHistory.length === 0 && (
        <p className="mc-empty">
          {isVideoFlow
            ? '还没有视频。选择或拍摄视频后，会先进入「待上传」。'
            : '还没有图片。拍照或从相册选择后，会先进入「待上传」。'}
        </p>
      )}

      {previewItem && (
        <div
          className="mc-preview-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="mc-pending-preview"
          onClick={() => setPreviewId(null)}
        >
          <div className="mc-preview-card" onClick={(e) => e.stopPropagation()}>
            {renderThumb(previewItem.previewUrl, isVideoFile(previewItem.file))}
            <div className="mc-preview-actions">
              <button type="button" className="mc-retry-btn" onClick={() => setPreviewId(null)}>
                关闭
              </button>
              <button
                type="button"
                className="mc-retry-btn mc-danger"
                onClick={() => {
                  removePendingItem(previewItem.id)
                  setPreviewId(null)
                }}
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
